import React, { useCallback, useEffect, useState } from 'react'
import { Card, Spinner } from 'react-bootstrap'
import 'bootstrap/dist/css/bootstrap.min.css'
import { FaArrowLeft } from 'react-icons/fa'
import { LuGraduationCap } from 'react-icons/lu'
import { BsArrowClockwise, BsArrowRight } from 'react-icons/bs'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { useInstitute } from './commonComponents/InstituteContext'
import ApiEndpoints from './commonComponents/ApiEndpoints'

const FIELD_LABELS = {
  payment_method: 'Payment Method',
  transaction_amount: 'Transaction Amount',
  cash: 'Cash',
  online: 'Online',
  cheque: 'Cheque',
  transaction_date: 'Transaction Date',
  transaction_id: 'Transaction ID',
  discount: 'Discount',
  amount: 'Amount',
  fees_status: 'Fee Status',
  fees_name: 'Fee Name',
}

const formatDisplayDateTime = (dateStr) => {
  if (!dateStr) return '—'
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return dateStr
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatFieldValue = (key, value) => {
  if (value === null || value === undefined || value === '') return '—'
  const moneyKeys = ['transaction_amount', 'cash', 'online', 'cheque', 'discount', 'amount']
  if (moneyKeys.includes(key)) {
    const num = Number(value)
    if (!Number.isNaN(num)) return `₹ ${num}`
  }
  if (key === 'payment_method' || key === 'fees_status') {
    return String(value).replace(/_/g, ' ')
  }
  return String(value)
}

const getChangedFields = (oldValues = {}, newValues = {}) => {
  const keys = Array.from(
    new Set([...Object.keys(oldValues || {}), ...Object.keys(newValues || {})]),
  )
  return keys.map((key) => ({
    key,
    label: FIELD_LABELS[key] || key.replace(/_/g, ' '),
    oldValue: oldValues?.[key],
    newValue: newValues?.[key],
  }))
}

const extractLogs = (response) => {
  const body = response?.data
  if (Array.isArray(body?.data)) return body.data
  if (Array.isArray(body)) return body
  return []
}

const getApiErrorMessage = (error, fallback) => {
  const data = error?.response?.data
  if (data?.message) return data.message
  if (data?.errors && typeof data.errors === 'object') {
    const firstError = Object.values(data.errors).flat()[0]
    if (firstError) return firstError
  }
  return fallback
}

export default function FeeChangeLogs() {
  const { logout } = useAuth()
  const { isCollapsed } = useSidebar()
  const { logo } = useInstitute()
  const navigate = useNavigate()
  const location = useLocation()
  const { fee_id } = useParams()

  const navState = location.state || {}
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
      setIsMobile(window.innerWidth <= 768)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  const fetchLogs = useCallback(async () => {
    if (!fee_id) {
      toast.error('Fee ID is missing.')
      setLogs([])
      setLoading(false)
      return
    }

    setLoading(true)
    try {
      const response = await ApiEndpoints.getFeeChangeLogs({ fee_id })
      console.log('Fee change logs API response:', response?.data)
      setLogs(extractLogs(response))
    } catch (error) {
      console.error('Failed to load fee change logs:', error)
      toast.error(getApiErrorMessage(error, 'Failed to load fee change logs.'))
      setLogs([])
    } finally {
      setLoading(false)
    }
  }, [fee_id])

  useEffect(() => {
    fetchLogs()
  }, [fetchLogs])

  const firstLog = logs[0]
  const studentName =
    firstLog?.student?.name ||
    navState.studentName ||
    '—'
  const admissionNumber =
    firstLog?.student?.admission_number ||
    navState.admissionNumber ||
    '—'
  const feeName =
    firstLog?.fee?.fees_name ||
    navState.feeName ||
    '—'
  const feeAmount =
    firstLog?.fee?.amount ||
    navState.feeAmount ||
    '—'
  const feeStatus =
    firstLog?.fee?.fees_status ||
    navState.feeStatus ||
    '—'

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1)
    } else {
      navigate('/ManageStudentFees')
    }
  }

  return (
    <>
      <div
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          paddingLeft: isCollapsed ? '12px' : '12px',
          paddingRight: isCollapsed ? '12px' : '12px',
        }}
      >
        <div
          className="container-fluid p-0"
          style={{
            marginTop: window.innerWidth < 992 ? '55px' : '-12px',
          }}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                padding: '12px 28px',
                borderBottom: '1px solid #D1D3D4',
              }}
            >
              <div className="d-flex align-items-center gap-3">
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {!logoError ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                  )}
                </div>
                <h3
                  className="m-0 fw-bold"
                  style={{
                    fontSize: 'clamp(18px, 2.5vw, 22px)',
                    color: '#223843',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  Fee Update Logs
                </h3>
              </div>
              {!isMobile && <ProfileDropdown logout={logout} />}
            </div>
          </div>
        </div>

        <div
          className="container-fluid p-0 mt-4"
          style={{
            minHeight: '100vh',
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingTop: '24px',
            paddingBottom: '32px',
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
            <div className="d-flex align-items-center gap-3">
              <button
                type="button"
                onClick={handleBack}
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  backgroundColor: '#ffffff',
                  border: '1px solid #D1D3D4',
                  color: '#223843',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
                title="Back"
              >
                <FaArrowLeft size={18} />
              </button>
              <div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: '#111827',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  View Changes
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: '#6B7280',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  Fee ID: {fee_id || '—'}
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={fetchLogs}
              disabled={loading}
              style={{
                height: 42,
                borderRadius: 8,
                backgroundColor: '#223843',
                border: 'none',
                color: '#ffffff',
                padding: '0 16px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 13,
                fontWeight: 600,
                fontFamily: 'Inter, system-ui, sans-serif',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
              }}
            >
              <BsArrowClockwise size={16} />
              Refresh
            </button>
          </div>

          <Card
            className="mb-4 border-0"
            style={{
              borderRadius: 12,
              border: '1px solid #D1D3D4',
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
            }}
          >
            <Card.Body style={{ padding: 20 }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
                  gap: 16,
                }}
              >
                <div>
                  <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 4 }}>Student</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
                    {String(studentName).toUpperCase()}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 4 }}>
                    Admission No.
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
                    {admissionNumber}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 4 }}>Fee Name</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{feeName}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 4 }}>
                    Amount / Status
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
                    ₹ {feeAmount}{' '}
                    <span
                      style={{
                        marginLeft: 8,
                        backgroundColor:
                          String(feeStatus).toLowerCase() === 'complete' ? '#d1fae5' : '#fef3c7',
                        color:
                          String(feeStatus).toLowerCase() === 'complete' ? '#065f46' : '#92400e',
                        padding: '2px 8px',
                        borderRadius: 20,
                        fontSize: 12,
                        textTransform: 'capitalize',
                      }}
                    >
                      {feeStatus}
                    </span>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {loading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" style={{ color: '#223843' }} />
            </div>
          ) : logs.length === 0 ? (
            <Card
              className="border-0"
              style={{
                borderRadius: 12,
                border: '1px solid #D1D3D4',
              }}
            >
              <Card.Body className="text-center py-5">
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 600,
                    color: '#111827',
                    marginBottom: 6,
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  No change logs found
                </div>
                <div style={{ fontSize: 13, color: '#6B7280' }}>
                  Updates made via Edit Fee will appear here with before / after values.
                </div>
              </Card.Body>
            </Card>
          ) : (
            <div className="d-flex flex-column gap-3">
              {logs.map((log, index) => {
                const changedFields = getChangedFields(log.old_values, log.new_values)
                return (
                  <Card
                    key={log.id || index}
                    className="border-0"
                    style={{
                      borderRadius: 12,
                      border: '1px solid #D1D3D4',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        background: '#F9FAFB',
                        borderBottom: '1px solid #E5E7EB',
                        padding: '14px 18px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 10,
                      }}
                    >
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <span
                          style={{
                            background: '#223843',
                            color: '#fff',
                            borderRadius: 20,
                            padding: '3px 10px',
                            fontSize: 12,
                            fontWeight: 700,
                            textTransform: 'capitalize',
                          }}
                        >
                          {log.action || 'update'}
                        </span>
                        {/* <span style={{ fontSize: 13, color: '#6B7280' }}>
                          Log #{log.id} · Fee #{log.fee_id}
                        </span> */}
                      </div>
                      <div style={{ fontSize: 13, color: '#6B7280' }}>
                        {formatDisplayDateTime(log.created_at)}
                      </div>
                    </div>

                    <Card.Body style={{ padding: 18 }}>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
                          gap: 12,
                          marginBottom: 16,
                        }}
                      >
                        <div
                          style={{
                            background: '#F9FAFB',
                            borderRadius: 8,
                            padding: 12,
                            border: '1px solid #E5E7EB',
                          }}
                        >
                          <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 4 }}>
                            Changed By
                          </div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
                            {log.changed_by?.name || '—'}
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              color: '#6B7280',
                              textTransform: 'capitalize',
                              marginTop: 2,
                            }}
                          >
                            {log.changed_by?.user_type || '—'}
                            {log.changed_by?.id ? ` · ID ${log.changed_by.id}` : ''}
                          </div>
                        </div>

                        <div
                          style={{
                            background: '#F9FAFB',
                            borderRadius: 8,
                            padding: 12,
                            border: '1px solid #E5E7EB',
                          }}
                        >
                          <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 4 }}>
                            Student
                          </div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
                            {log.student?.name || '—'}
                          </div>
                          <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                            {log.student?.admission_number || '—'}
                            {log.student_id ? ` · ID ${log.student_id}` : ''}
                          </div>
                        </div>

                        <div
                          style={{
                            background: '#F9FAFB',
                            borderRadius: 8,
                            padding: 12,
                            border: '1px solid #E5E7EB',
                          }}
                        >
                          <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 4 }}>
                            Fee
                          </div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
                            {log.fee?.fees_name || '—'}
                          </div>
                          <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                            ₹ {log.fee?.amount ?? '—'} · {log.fee?.fees_status || '—'}
                            {log.institute_id ? ` · Inst ${log.institute_id}` : ''}
                          </div>
                        </div>
                      </div>

                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: '#111827',
                          marginBottom: 10,
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        Changed Fields
                      </div>

                      {changedFields.length === 0 ? (
                        <div style={{ fontSize: 13, color: '#6B7280' }}>
                          No field-level changes recorded.
                        </div>
                      ) : (
                        <div className="d-flex flex-column gap-2">
                          {changedFields.map((field) => (
                            <div
                              key={`${log.id}-${field.key}`}
                              style={{
                                display: 'grid',
                                gridTemplateColumns: isMobile
                                  ? '1fr'
                                  : '160px 1fr auto 1fr',
                                gap: 10,
                                alignItems: 'center',
                                padding: '10px 12px',
                                borderRadius: 8,
                                border: '1px solid #E5E7EB',
                                background: '#ffffff',
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: '#374151',
                                  textTransform: 'capitalize',
                                }}
                              >
                                {field.label}
                              </div>
                              <div
                                style={{
                                  fontSize: 13,
                                  color: '#991B1B',
                                  background: '#FEF2F2',
                                  borderRadius: 6,
                                  padding: '8px 10px',
                                  wordBreak: 'break-word',
                                }}
                              >
                                <span style={{ fontSize: 10, fontWeight: 700, opacity: 0.7 }}>
                                  BEFORE
                                </span>
                                <div>{formatFieldValue(field.key, field.oldValue)}</div>
                              </div>
                              {!isMobile && (
                                <BsArrowRight size={16} style={{ color: '#9CA3AF' }} />
                              )}
                              <div
                                style={{
                                  fontSize: 13,
                                  color: '#065F46',
                                  background: '#ECFDF5',
                                  borderRadius: 6,
                                  padding: '8px 10px',
                                  wordBreak: 'break-word',
                                }}
                              >
                                <span style={{ fontSize: 10, fontWeight: 700, opacity: 0.7 }}>
                                  AFTER
                                </span>
                                <div>{formatFieldValue(field.key, field.newValue)}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      <details style={{ marginTop: 14 }}>
                        <summary
                          style={{
                            cursor: 'pointer',
                            fontSize: 12,
                            fontWeight: 600,
                            color: '#6B7280',
                            userSelect: 'none',
                          }}
                        >
                          Raw old_values / new_values
                        </summary>
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                            gap: 10,
                            marginTop: 10,
                          }}
                        >
                          <pre
                            style={{
                              margin: 0,
                              background: '#FEF2F2',
                              border: '1px solid #FECACA',
                              borderRadius: 8,
                              padding: 12,
                              fontSize: 11,
                              overflow: 'auto',
                              color: '#7F1D1D',
                            }}
                          >
                            {JSON.stringify(log.old_values || {}, null, 2)}
                          </pre>
                          <pre
                            style={{
                              margin: 0,
                              background: '#ECFDF5',
                              border: '1px solid #A7F3D0',
                              borderRadius: 8,
                              padding: 12,
                              fontSize: 11,
                              overflow: 'auto',
                              color: '#064E3B',
                            }}
                          >
                            {JSON.stringify(log.new_values || {}, null, 2)}
                          </pre>
                        </div>
                      </details>
                    </Card.Body>
                  </Card>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
